import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import '../consts/events.quiz-folder';

import { trackEvent } from '@zep/logger/tracking';
import { zusToast } from '@zep/toast';

import { useOpenWindow } from '@/hooks/useLocale';

import { useRemoveFolderItem } from '../api';
import type { FolderSavePlace } from '../consts/events.quiz-folder';
import {
  reportFolderError,
  toFolderSaveContext,
  toTrackingFailReason,
} from '../utils/folderTracking';
import { showQuizSaveSuccess } from '../utils/quizSaveFeedback';

/**
 * 퀴즈를 지금 폴더에서 빼서 기본 `내 퀴즈` 로 옮긴다 — 저장 위치 모달 없이 바로.
 *
 * 모달을 거치는 이동은 `useSaveQuizLocationFlow`(`location.kind === 'default'`)가 맡고, 이 훅은
 * "위치를 고를 필요가 없는" 자리(공개 폴더 안 퀴즈를 숨기려는 빌드 설정 안내)에서 같은 결과 안내와
 * 같은 저장 흐름 이벤트(`entry_click` → `save_location_save`)를 쓴다.
 * 목록 갱신은 제거 뮤테이션의 무효화가 처리한다.
 */
export const useMoveQuizToDefault = ({
  quizSetId,
  folderId,
  place,
  onMoved,
  openResultInNewTab = false,
}: {
  quizSetId: string;
  /** 지금 속한 폴더. 없으면 뺄 곳이 없어 아무것도 하지 않는다. */
  folderId: string | undefined;
  place: FolderSavePlace;
  onMoved?: () => void;
  /** 완료 토스트의 `보러 가기` 를 새 탭으로 연다. 작업 중인 화면을 벗어나면 안 되는 곳에서 쓴다. */
  openResultInNewTab?: boolean;
}) => {
  const { t } = useTranslation(['common']);
  const router = useRouter();
  const openWindow = useOpenWindow();
  const removeItem = useRemoveFolderItem();

  const move = () => {
    if (!folderId) return;
    // 내 퀴즈를 옮기는 자리라 소유자다.
    const context = toFolderSaveContext({
      id: quizSetId,
      intent: 'move',
      place,
      isQuizOwner: true,
    });
    trackEvent('quiz_folder_save_entry_click', {
      ...context,
      auth_required: false,
    });
    const saveProps = {
      ...context,
      location_type: 'default',
      from_folder_id: folderId,
      publish_confirmed: false,
    } as const;

    void (async () => {
      try {
        await removeItem.mutateAsync({ id: folderId, quizsetId: quizSetId });
        trackEvent('quiz_folder_save_location_save', {
          ...saveProps,
          result: 'success',
        });
        showQuizSaveSuccess({
          title: t('common:folderSave.moveToDefaultSuccess'),
          href: '/myquiz',
          actionLabel: t('common:folderSave.viewFolder'),
          onNavigate: destination => {
            if (openResultInNewTab) {
              openWindow(destination);
              return;
            }
            void router.push(destination);
          },
        });
        onMoved?.();
      } catch (error) {
        trackEvent('quiz_folder_save_location_save', {
          ...saveProps,
          result: 'fail',
          reason: toTrackingFailReason(error),
        });
        reportFolderError(error, {
          action: 'save_location_move',
          folderId,
          quizSetId,
        });
        zusToast.error(t('common:folderSave.error'), {
          position: 'top-center',
        });
      }
    })();
  };

  return { move, isMoving: removeItem.isPending };
};
